import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const formSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  company: z.string().optional(),
  role: z.string().optional(),
  useCase: z.string().optional(),
  acceptContact: z.boolean().optional(),
});

const Schedule = () => {
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data) => {
    setStatus('loading');

    try {
      const apiUrl = process.env.REACT_APP_API_ENDPOINT || 'https://7m0zw17tt4.execute-api.us-east-1.amazonaws.com/prod/waitlist';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          company: data.company || '',
          role: data.role || '',
          useCase: data.useCase || '',
          acceptContact: data.acceptContact ? 'true' : 'false',
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(result.message || 'Success! Check your email to confirm.');
        reset();
      } else {
        setStatus('error');
        setMessage(result.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <div>
      <Navigation />

      {/* Hero Section */}
      <section className="hero-section pt-32 pb-16 bg-gradient-to-br from-gray-900 via-onyx-900 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl text-white mb-6">
              Schedule a Consultation
            </h1>
            <p className="text-xl text-white max-w-3xl mx-auto">
              Let's discuss how Onyx Rose Advisors can help your organization achieve its goals.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 sm:p-12">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-gray-900 mb-2">
                    First Name <span className="text-accent">*</span>
                  </label>
                  <input
                    {...register('firstName')}
                    type="text"
                    id="firstName"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-accent focus:border-accent transition-colors duration-200 outline-none"
                    placeholder="John"
                  />
                  {errors.firstName && (
                    <p className="text-accent text-sm mt-1">{errors.firstName.message}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-gray-900 mb-2">
                    Last Name <span className="text-accent">*</span>
                  </label>
                  <input
                    {...register('lastName')}
                    type="text"
                    id="lastName"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-accent focus:border-accent transition-colors duration-200 outline-none"
                    placeholder="Doe"
                  />
                  {errors.lastName && (
                    <p className="text-accent text-sm mt-1">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address <span className="text-accent">*</span>
                </label>
                <input
                  {...register('email')}
                  type="email"
                  id="email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-accent focus:border-accent transition-colors duration-200 outline-none"
                  placeholder="john.doe@company.com"
                />
                {errors.email && (
                  <p className="text-accent text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              {/* Company Name */}
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                  Company Name
                </label>
                <input
                  {...register('company')}
                  type="text"
                  id="company"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-accent focus:border-accent transition-colors duration-200 outline-none"
                  placeholder="Your Company"
                />
              </div>

              {/* Role */}
              <div>
                <label htmlFor="role" className="block text-sm font-semibold text-gray-900 mb-2">
                  Your Role
                </label>
                <input
                  {...register('role')}
                  type="text"
                  id="role"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-accent focus:border-accent transition-colors duration-200 outline-none"
                  placeholder="Director of Operations"
                />
              </div>

              {/* Services Interest */}
              <div>
                <label htmlFor="useCase" className="block text-sm font-semibold text-gray-900 mb-2">
                  Services of Interest
                </label>
                <textarea
                  {...register('useCase')}
                  id="useCase"
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-accent focus:border-accent transition-colors duration-200 outline-none"
                  placeholder="Please describe the types of services you're interested in..."
                />
              </div>

              {/* Accept Contact Checkbox */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    {...register('acceptContact')}
                    type="checkbox"
                    id="acceptContact"
                    className="w-4 h-4 border border-gray-300 rounded text-accent focus:ring-accent focus:ring-2"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="acceptContact" className="text-sm text-gray-700">
                    I agree to receive communications from Onyx Rose Advisors regarding my inquiry and related services.
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className={`w-full bg-accent text-white font-semibold px-8 py-4 rounded-md hover:bg-accent-700 transition-all duration-200 shadow-lg hover:shadow-xl ${
                    status === 'loading' ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {status === 'loading' ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>

              {/* Success Message */}
              {status === 'success' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-green-800">{message}</p>
                </div>
              )}

              {/* Error Message */}
              {status === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-800">{message}</p>
                </div>
              )}

              <p className="text-sm text-gray-500 text-center">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </form>
          </div>

          {/* Additional Info */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Prefer to reach out directly? Email us at{' '}
              <a href="mailto:info@onyxroseadvisors.com" className="text-accent hover:text-accent-700 font-semibold">
                info@onyxroseadvisors.com
              </a>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Schedule;
